import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, Play, Pause, Upload, Trash2, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { BriefBulletsAsiaService } from "@/services/briefBullets/briefBulletsAsiaService";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import { SupabaseStorageService } from "@/services/photo/supabaseStorageService";
import { supabase } from "@/integrations/supabase/client";
import { PromptViewer } from "./PromptViewer";
import { BRIEFR_BULLETS_PROMPTS, SCHEDULE_INFO } from "@/constants/briefBulletsPrompts";

export function BriefBulletsAsiaManager() {
  const queryClient = useQueryClient();
  const [photoUrl, setPhotoUrl] = useState<string>("/images/asia-map.png");
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const { data: taskStatus, isLoading } = useQuery({
    queryKey: ["briefBulletsAsiaTask"],
    queryFn: BriefBulletsAsiaService.getTaskStatus,
    refetchInterval: 30000,
  });

  const { data: weekStatus } = useQuery({
    queryKey: ["briefBulletsAsiaWeek"],
    queryFn: BriefBulletsAsiaService.getThisWeekStatus,
    refetchInterval: 30000,
  });

  const { data: publishHistory } = useQuery({
    queryKey: ["briefBulletsAsiaHistory"],
    queryFn: () => BriefBulletsAsiaService.getPublishHistory(4),
  });

  const { data: taskLogs } = useQuery({
    queryKey: ["briefBulletsAsiaLogs"],
    queryFn: () => BriefBulletsAsiaService.getTaskLogs(10),
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (taskStatus?.config?.photo_url && taskStatus.config.photo_url.trim() !== '') {
      setPhotoUrl(taskStatus.config.photo_url);
    } else if (taskStatus) {
      setPhotoUrl("/images/asia-map.png");
    }
  }, [taskStatus]);

  const publishMutation = useMutation({
    mutationFn: BriefBulletsAsiaService.triggerManualPublish,
    onSuccess: (result) => {
      if (result.success) {
        toast.success("Artikkel publisert!");
        queryClient.invalidateQueries({ queryKey: ["briefBulletsAsiaWeek"] });
        queryClient.invalidateQueries({ queryKey: ["briefBulletsAsiaHistory"] });
        queryClient.invalidateQueries({ queryKey: ["briefBulletsAsiaLogs"] });
      } else {
        toast.error(`Publisering feilet: ${result.error}`);
      }
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (enable: boolean) =>
      BriefBulletsAsiaService.toggleAutomaticPublishing(enable),
    onSuccess: (success, enable) => {
      if (success) {
        toast.success(
          enable
            ? "Automatisk publisering aktivert"
            : "Automatisk publisering pauset"
        );
        queryClient.invalidateQueries({ queryKey: ["briefBulletsAsiaTask"] });
      } else {
        toast.error("Kunne ikke endre innstillinger");
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: BriefBulletsAsiaService.deleteThisWeekArticle,
    onSuccess: (result) => {
      if (result.success) {
        toast.success("Artikkel slettet");
        queryClient.invalidateQueries({ queryKey: ["briefBulletsAsiaWeek"] });
        queryClient.invalidateQueries({ queryKey: ["briefBulletsAsiaHistory"] });
      } else {
        toast.error(`Sletting feilet: ${result.error}`);
      }
    },
  });

  const handleToggleAutoPublish = () => {
    const isActive = taskStatus?.status === 'active';
    toggleMutation.mutate(!isActive);
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploadingPhoto(true);
    try {
      const uploadedUrl = await SupabaseStorageService.uploadImage(file, "profile-images");
      setPhotoUrl(uploadedUrl);

      const { error } = await supabase.rpc("update_briefr_bullets_asia_config", {
        new_photo_url: uploadedUrl,
      });

      if (error) throw error;

      toast.success("Profilbilde oppdatert");
      queryClient.invalidateQueries({ queryKey: ["briefBulletsAsiaTask"] });
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Kunne ikke laste opp bilde");
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Briefr Bullets: Asia</CardTitle>
        <CardDescription>
          Automatisk publisering hver onsdag kl. 05:30
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Prompt Viewer */}
        <PromptViewer 
          taskName="Asia"
          prompt={BRIEFR_BULLETS_PROMPTS.asia}
          config={taskStatus?.config}
          scheduleInfo={SCHEDULE_INFO.asia}
        />

        {/* Current Status */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Status denne uken</h3>
            {weekStatus?.published ? (
              <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10">
                <CheckCircle2 className="h-3 w-3 text-green-600" />
                <span className="text-sm text-green-600 font-medium">Publisert</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-muted">
                <Clock className="h-3 w-3" />
                <span className="text-sm">Venter</span>
              </div>
            )}
          </div>

          {weekStatus?.article && (
            <div className="text-sm text-muted-foreground">
              Publisert {format(new Date(weekStatus.article.timestamp), 'dd. MMMM yyyy', { locale: nb })} kl. {format(new Date(weekStatus.article.timestamp), 'HH:mm', { locale: nb })}
            </div>
          )}

          {taskStatus?.error_message && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Siste feil:</p>
                <p>{taskStatus.error_message}</p>
                {taskStatus.retry_count > 0 && (
                  <p className="mt-1 text-xs">
                    Forsøk {taskStatus.retry_count} av {taskStatus.config?.max_retries || 1}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Task Configuration */}
        {taskStatus && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Konfigurasjon</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Status:</span>
                <div className="mt-1">
                  {taskStatus.status === 'active' ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-600">
                      Aktiv
                    </span>
                  ) : taskStatus.status === 'paused' ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted">
                      Pauset
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-destructive/10 text-destructive">
                      Feilet
                    </span>
                  )}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Publiseringstid:</span>
                <div className="mt-1 font-medium">Onsdager kl. {taskStatus.schedule_time?.slice(0, 5)}</div>
              </div>
              {taskStatus.next_run && (
                <div>
                  <span className="text-muted-foreground">Neste kjøring:</span>
                  <div className="mt-1 font-medium">
                    {format(new Date(taskStatus.next_run), 'dd.MM HH:mm', { locale: nb })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Photo Upload */}
        <div className="space-y-2 pt-6 border-t">
          <h3 className="font-medium">Profilbilde for Kai</h3>
          <div className="flex items-center gap-3">
            <img 
              src={photoUrl} 
              alt="Kai profil" 
              className="w-16 h-16 rounded-full object-cover border-2 border-border"
            />
            <div className="flex-1">
              <input
                type="file"
                id="photo-upload-asia"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                disabled={isUploadingPhoto}
                onClick={() => document.getElementById('photo-upload-asia')?.click()}
              >
                {isUploadingPhoto ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Laster opp...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Last opp nytt bilde
                  </>
                )}
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Dette bildet brukes som profilbilde i Asia-artiklene
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <div className="flex gap-2">
            <Button
              onClick={() => publishMutation.mutate()}
              disabled={publishMutation.isPending}
              className="flex-1"
            >
              {publishMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Publiserer...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Publiser nå
                </>
              )}
            </Button>
            <Button
              onClick={() => queryClient.invalidateQueries()}
              variant="outline"
              size="icon"
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleToggleAutoPublish}
              disabled={toggleMutation.isPending}
              variant={taskStatus?.status === 'paused' ? 'default' : 'secondary'}
              className="flex-1"
            >
              {toggleMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Endrer...
                </>
              ) : taskStatus?.status === 'paused' ? (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Aktiver automatisk publisering
                </>
              ) : (
                <>
                  <Pause className="h-4 w-4 mr-2" />
                  Pause automatisk publisering
                </>
              )}
            </Button>
            <Button
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending || !weekStatus?.published}
              variant="destructive"
              size="icon"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* History */}
        {publishHistory && publishHistory.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Siste publiseringer</h3>
            <div className="space-y-2">
              {publishHistory.map((article) => (
                <div
                  key={article.id}
                  className="flex items-center justify-between p-2 rounded-lg border bg-card text-sm"
                >
                  <div>
                    <div className="font-medium">
                      {format(new Date(article.timestamp), 'dd. MMMM yyyy', { locale: nb })}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Kl. {format(new Date(article.timestamp), 'HH:mm', { locale: nb })}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10">
                    <CheckCircle2 className="h-3 w-3 text-green-600" />
                    <span className="text-sm text-green-600 font-medium">Publisert</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Logs */}
        {taskLogs && taskLogs.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Logg</h3>
            <div className="space-y-2">
              {taskLogs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-start gap-2 p-2 rounded-lg border bg-card text-sm"
                >
                  {log.status === 'completed' ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  ) : log.status === 'failed' ? (
                    <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                  ) : (
                    <Clock className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{log.message || log.status}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {format(new Date(log.created_at), 'dd.MM.yyyy HH:mm:ss', { locale: nb })}
                      {log.execution_time_ms && ` • ${log.execution_time_ms}ms`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
